import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { updatePageMeta } from '../utils/seo';
import { metaFor } from '../seo/routeMeta';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { bookingAPI, pricingAPI, travelAPI } from '../utils/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatPhoneNumber } from '../utils/phone';
import { getPricing, getDefaultPackages as getFallbackPackages, getDefaultAddOns, calculateSqftSurcharge, calculateSqftBonusPhotos, getMediaExpiryDays, isLandPackage, packagesFor, addOnsFor, parseAcres, landExtraAcres, calculateAcreageSurcharge, landAerialPhotoMinimum, getLandAcreageConfig, includedOnceIds, includedUnitsFor } from '../utils/pricing';
import './Book.css';

// One API base for the whole page (review §9 #6: it was declared four times,
// two of them without /api — broken for local development only).
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Book() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [packages, setPackages] = useState([]);
  // Live media-expiry window for the disclosure under the order total. Seeded
  // at 45 so the disclosure is never blank while pricing is in flight.
  const [mediaExpiryDays, setMediaExpiryDays] = useState(45);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  // Guest checkout (Pack B2): shown at the last step when nobody is signed in.
  const [showGuestChoice, setShowGuestChoice] = useState(false);
  // Set when a guest submits with an email that already has an account.
  const [accountExists, setAccountExists] = useState(false);
  // Pack A3.4: booking policy fetched from public settings endpoint.
  // Used to gate the date picker (filterDate prop) and bump the minDate
  // forward by minNoticeHours. Backend gates remain the source of truth.
  const [bookingPolicy, setBookingPolicy] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [discountCode, setDiscountCode] = useState('');
  const [discountResult, setDiscountResult] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [pendingDiscountRevalidation, setPendingDiscountRevalidation] = useState(false);
  const [travelFee, setTravelFee] = useState(0);
  const [travelFeeLoading, setTravelFeeLoading] = useState(false);
  const [travelInfo, setTravelInfo] = useState(null);
  const [isUtahCounty, setIsUtahCounty] = useState(true);

  // Add-ons are seeded from the synchronous fallback so the UI renders instantly.
  // getPricing() below refreshes them from the server once it resolves.
  const [addOnsList, setAddOnsList] = useState(getDefaultAddOns());

  const toggleAddOn = (id) => {
    setSelectedAddOns(prev => {
      const copy = { ...prev };
      if (copy[id]) delete copy[id];
      else copy[id] = true;
      return copy;
    });
  };

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    setApplyingDiscount(true);
    setDiscountError('');
    setDiscountResult(null);
    try {
      const resp = await fetch(`${API_URL}/discount/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Include clientEmail so the per-client usage limit is enforced
        // inline rather than only at submit time. If the field hasn't been
        // filled in yet, we send undefined and the server falls back to
        // its old behavior (per-client check skipped, only global checks).
        body: JSON.stringify({
          code: discountCode,
          orderAmount: (selectedPkg?.price || 0) + addOnsTotal,
          packageType: formData.selectedPackage,
          // Item-scoped codes need to know what's in the cart.
          addOns: addOnsList.filter(a => selectedAddOns[a.id]).map(a => a.id),
          clientEmail: formData.clientEmail || undefined
        })
      });
      const data = await resp.json();
      if (data.success) {
        setDiscountResult(data.data);
      } else {
        setDiscountError(data.message || 'Invalid code');
      }
    } catch (e) {
      setDiscountError('Error validating code');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const addOnsTotal = addOnsList.filter(a => selectedAddOns[a.id]).reduce((sum, a) => sum + a.price, 0);

  const [formData, setFormData] = useState({
    // Client Info
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    
    // Property Info — Pack A4: structured address fields. Sent to backend
    // separately so /complete-booking can persist them as discrete columns.
    propertyAddress: '',  // street line only (e.g. "2007 E Blossom St")
    propertyCity: '',
    propertyState: 'UT',
    propertyZip: '',
    propertyType: 'residential',
    squareFootage: '',
    bedrooms: '',
    bathrooms: '',
    acreage: '',   // land packages only (2026-10-11)
    
    // Booking Info
    selectedPackage: '',
    specialRequests: '',
    hearAboutUs: 'google'
  });

  const [searchParams] = useSearchParams();

  // ?package=<id> — from the /pricing page's "Book this package" buttons.
  // ?kind=land — from /services/land. Never overrides a choice already made
  // (e.g. a draft restored after sign-in).
  useEffect(() => {
    // 'land-acreage' was retired 2026-10-12 (one per-acre land package now);
    // an old link to it opens the land package.
    const raw = String(searchParams.get('package') || '').toLowerCase();
    const requested = raw === 'land-acreage' ? 'land-lot' : raw;
    const LAND_IDS = ['land-lot'];
    if (['basic', 'standard', 'premium', 'luxury', ...LAND_IDS].includes(requested)) {
      setFormData(prev => (prev.selectedPackage ? prev : {
        ...prev,
        selectedPackage: requested,
        propertyType: LAND_IDS.includes(requested) ? 'land' : prev.propertyType
      }));
    } else if (String(searchParams.get('kind') || '').toLowerCase() === 'land') {
      setFormData(prev => (prev.selectedPackage ? prev : { ...prev, propertyType: 'land' }));
    }
  }, [searchParams]);

  const selectedPkg = packages.find(p => p._id === formData.selectedPackage);

  // One-off items the chosen package already includes (its 3D tour, cinematic
  // video, floor plan, social reel) can't be added again — the server never
  // charges them either (2026-10-17). Counted items (drone, twilight, staging)
  // stay available as extras on top of what's included.
  const includedOnce = React.useMemo(() => new Set(includedOnceIds(selectedPkg)), [selectedPkg]);
  useEffect(() => {
    if (!includedOnce.size) return;
    setSelectedAddOns(prev => {
      const hits = Object.keys(prev).filter(id => includedOnce.has(id));
      if (!hits.length) return prev;
      const next = { ...prev };
      hits.forEach(id => { delete next[id]; });
      return next;
    });
  }, [includedOnce]);
  const pkgShortName = selectedPkg ? String(selectedPkg.name || '').replace(/\s*Package$/, '') : '';

  // Land packages (Thomas, 2026-10-11). "Land or lot" on step 1 swaps the
  // home packages for the land ones, trims add-ons to those that make sense
  // outdoors, and asks for acres instead of sq ft / beds / baths on step 3.
  const isLand = formData.propertyType === 'land';
  const visiblePackages = packagesFor(packages, isLand);
  const visibleAddOns = addOnsFor(addOnsList, isLand);
  // Land is priced by size (2026-10-12): the first acre is in the package
  // price, each additional acre or part of one adds the per-acre rate.
  const landCfg = getLandAcreageConfig();
  const landAcres = parseAcres(formData.acreage);
  const acreageSurcharge = isLand ? calculateAcreageSurcharge(selectedPkg, formData.acreage) : 0;
  const landPriceFor = (acres) => (packagesFor(packages, true)[0]?.price || 0) + landExtraAcres(acres) * landCfg.pricePerAcre;

  const chooseListingKind = (kind) => {
    const land = kind === 'land';
    if (land === isLand) return;
    setFormData(prev => ({
      ...prev,
      propertyType: land ? 'land' : 'residential',
      // A package from the other line no longer applies.
      selectedPackage: packages.some(p => p._id === prev.selectedPackage && isLandPackage(p) === land) ? prev.selectedPackage : '',
      squareFootage: land ? '' : prev.squareFootage,
      bedrooms: land ? '' : prev.bedrooms,
      bathrooms: land ? '' : prev.bathrooms,
      acreage: land ? prev.acreage : ''
    }));
    if (land) {
      const allowed = new Set(addOnsFor(addOnsList, true).map(a => a.id));
      setSelectedAddOns(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => allowed.has(id))));
    }
  };

  // Bundle-savings nudge. Someone stacking à la carte items onto a small
  // package often ends up paying MORE than the tier that already includes
  // them. Packages declare `includedAddOns`, so this is exact arithmetic
  // rather than a guess — and it only ever appears when upgrading is genuinely
  // cheaper, so it reads as help rather than a sales push.
  const bundleUpsell = React.useMemo(() => {
    if (!selectedPkg) return null;
    const chosen = addOnsList.filter(a => selectedAddOns[a.id]);
    if (chosen.length === 0) return null;

    const currentTotal = (selectedPkg.price || 0) + chosen.reduce((sum, a) => sum + a.price, 0);
    let best = null;

    for (const pkg of packages) {
      if (!pkg || pkg.price <= (selectedPkg.price || 0)) continue;
      // Never suggest a home package for a land shoot, or the reverse.
      if (isLandPackage(pkg) !== isLandPackage(selectedPkg)) continue;
      const included = pkg.includedAddOns || [];
      const covered = chosen.filter(a => included.includes(a.id));
      if (covered.length === 0) continue;
      const remaining = chosen.filter(a => !included.includes(a.id));
      const upgradeTotal = pkg.price + remaining.reduce((sum, a) => sum + a.price, 0);
      const saving = currentTotal - upgradeTotal;
      if (saving > 0 && (!best || saving > best.saving)) {
        best = { pkg, covered, saving, upgradeTotal };
      }
    }
    return best;
  }, [selectedPkg, packages, addOnsList, selectedAddOns]);

  const applyBundleUpsell = () => {
    if (!bundleUpsell) return;
    const included = bundleUpsell.pkg.includedAddOns || [];
    setSelectedAddOns(prev => {
      const next = { ...prev };
      included.forEach(id => { delete next[id]; });
      return next;
    });
    setFormData(prev => ({ ...prev, selectedPackage: bundleUpsell.pkg._id }));
  };

  // Pack J.1: subscription savings mapping. Keyed by package id (and known
  // aliases). When a sub-eligible package is selected AND the customer is
  // not already a subscriber, the Order Summary surfaces the "you'd save
  // $X/month with a subscription" pitch. Numbers match server/config/pricing.js
  // × 4 shoots compared against the subscription monthly price (sources:
  // server/models/Subscription.getPlanDetails and SubscriptionPlans.jsx).
  // Essential / basic NOT eligible — not a subscription tier.
  // The subscription product is retired from public view (code intact — see
  // HANDOFF standing reminders). The summary nudges prepaid credit instead.
  const showCreditNudge = !!selectedPkg;
  const discountAmount = discountResult?.discountAmount || 0;
  // Square-footage surcharge preview (Essential/Professional over the live threshold, 2,500 sq ft by default).
  // Server recomputes authoritatively on submit; this keeps the displayed total
  // honest as the customer enters square footage.
  const sqftSurcharge = calculateSqftSurcharge(formData.selectedPackage, formData.squareFootage);
  const sqftBonusPhotos = calculateSqftBonusPhotos(formData.selectedPackage, formData.squareFootage);
  const estimatedTotal = Math.max(0, (selectedPkg?.price || 0) + sqftSurcharge + acreageSurcharge + addOnsTotal + travelFee - discountAmount);

  useEffect(() => {
    updatePageMeta(metaFor('/book'));
    fetchPackages();

    // Pack A3.4: Fetch booking policy so the date/time pickers can hide
    // invalid options up-front (weekends if disabled, dates within the
    // min-notice window). Failure is non-fatal — if the fetch fails, the
    // pickers fall back to showing everything and the backend gates catch
    // any invalid submissions.
    (async () => {
      try {
        const resp = await axios.get(`${API_URL}/settings`);
        if (resp?.data?.data?.bookingPolicy) {
          setBookingPolicy(resp.data.data.bookingPolicy);
        }
      } catch (err) {
        // Silent — pickers will show full options and backend gates will block invalid attempts
      }
    })();

    // Restore booking state if returning from login
    const bookingInProgress = localStorage.getItem('bookingInProgress');
    if (bookingInProgress) {
      const savedBooking = JSON.parse(bookingInProgress);
      setFormData(savedBooking.formData);
      if (savedBooking.selectedDate) {
        setSelectedDate(new Date(savedBooking.selectedDate));
      }
      if (savedBooking.selectedTime) {
        setSelectedTime(savedBooking.selectedTime);
      }
      if (savedBooking.selectedAddOns) {
        setSelectedAddOns(savedBooking.selectedAddOns);
      }
      if (savedBooking.discountCode) {
        setDiscountCode(savedBooking.discountCode);
        // Defer re-validation — packages are still loading at this point, so we
        // can't compute orderAmount yet. A dedicated useEffect will fire once
        // packages load and will re-validate the code against the server.
        setPendingDiscountRevalidation(true);
      }
      setStep(savedBooking.step); // Stay at same step
      localStorage.removeItem('bookingInProgress');
      toast.info('Welcome back! Click "Complete Booking" to finish.');
    } else {
      // Auto-fill client info from logged-in user if available
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user) {
          setFormData(prev => ({
            ...prev,
            clientName: prev.clientName || user.name || '',
            clientEmail: prev.clientEmail || user.email || '',
            clientPhone: formatPhoneNumber(prev.clientPhone || user.phone || '')
          }));
        }
      } catch (e) { /* not logged in, that's fine */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    // DEBOUNCED. squareFootage is an uncontrolled-feel number input with no
    // debounce of its own, so typing "2400" produced FOUR renders and, before
    // 2026-08-05, four availability requests. Add package comparisons, add-on
    // toggles and a few date picks and a single customer exceeded the booking
    // rate limit BEFORE reaching checkout — at which point completing the
    // booking and creating the Stripe session both returned 429 and the sale
    // was lost silently.
    //
    // The limiter split is the safety net; this is the actual fix. 400ms is
    // below the threshold where a slot grid feels laggy and comfortably longer
    // than a keystroke.
    const t = setTimeout(() => { fetchAvailability(); }, 400);
    return () => clearTimeout(t);
    // Re-fetch when the date OR anything that changes this booking's duration
    // changes (package, square footage, add-on count) — so the greyed-out slots
    // always reflect the real shoot length the customer has configured.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, formData.selectedPackage, formData.squareFootage, formData.acreage, Object.keys(selectedAddOns).length]);

  // Re-validate a restored discount code once packages have loaded, so the
  // request carries the correct orderAmount and the server can check minimums
  // and recompute the discount.
  useEffect(() => {
    if (!pendingDiscountRevalidation) return;
    if (!packages || packages.length === 0) return;
    if (!discountCode) { setPendingDiscountRevalidation(false); return; }

    let cancelled = false;
    (async () => {
      try {
        const pkg = packages.find(p => p._id === formData.selectedPackage || p.id === formData.selectedPackage);
        const pkgPrice = pkg?.price || 0;
        const currentAddOnsTotal = addOnsList
          .filter(a => selectedAddOns[a.id])
          .reduce((s, a) => s + a.price, 0);
        const resp = await fetch(`${API_URL}/discount/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: discountCode,
            orderAmount: pkgPrice + currentAddOnsTotal,
            packageType: formData.selectedPackage,
            // Item-scoped codes need to know what's in the cart.
            addOns: addOnsList.filter(a => selectedAddOns[a.id]).map(a => a.id),
            // Include clientEmail so the per-client usage limit is enforced
            // during re-validation (e.g. after page reload with a saved code).
            clientEmail: formData.clientEmail || undefined
          })
        });
        const data = await resp.json();
        if (cancelled) return;
        if (data.success) {
          setDiscountResult(data.data);
        } else {
          setDiscountError(data.message || 'Your discount code is no longer valid — please re-enter it');
        }
      } catch {
        if (!cancelled) setDiscountError('Could not re-validate discount code — please re-enter it');
      } finally {
        if (!cancelled) setPendingDiscountRevalidation(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages, pendingDiscountRevalidation]);

  const fetchPackages = async () => {
    try {
      const response = await pricingAPI.getAll({ active: true });
      // API returns { success, data: [...packages] }
      const pkgs = response.data?.data || response.data;

      if (Array.isArray(pkgs) && pkgs.length > 0) {
        setPackages(pkgs);
      } else {
        // Fall back to the centralized helper (matches server/config/pricing.js)
        setPackages(getFallbackPackages());
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      setPackages(getFallbackPackages());
    }

    // Also refresh add-ons from the server (cached after first call)
    try {
      const pricing = await getPricing();
      if (Array.isArray(pricing?.addOns) && pricing.addOns.length > 0) {
        setAddOnsList(pricing.addOns);
      }
      setMediaExpiryDays(getMediaExpiryDays());
    } catch (e) {
      // Add-ons already seeded with fallback — nothing to do
    }
  };

  const fetchAvailability = async () => {
    if (!selectedDate) {
      return;
    }

    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      // Pack A4/A5: pass package + sqft + add-on count so the server computes
      // THIS booking's real duration and returns per-slot availability flags.
      const response = await bookingAPI.getAvailability(dateStr, {
        package: formData.selectedPackage,
        squareFootage: formData.squareFootage ? parseInt(formData.squareFootage) : undefined,
        acreage: parseAcres(formData.acreage) || undefined,
        addOnsCount: Object.keys(selectedAddOns).length
      });

      // Use the per-slot availability array (each { time, available }) so we can
      // render unavailable slots as greyed/disabled rather than hiding them.
      const arr = (response.data && Array.isArray(response.data.availability))
        ? response.data.availability
        : [];

      const normalized = arr
        .map(s => {
          const timeString = (s && typeof s === 'object' && s.time) ? s.time
            : (typeof s === 'string' ? s : null);
          if (!timeString) return null;
          return { time: formatTimeSlot(timeString), available: s.available !== false };
        })
        .filter(Boolean)
        // Keep 10 AM onward (matches the displayed business-hours window)
        .filter(s => {
          const m = s.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
          if (!m) return true;
          let h = parseInt(m[1]);
          const mer = m[3].toUpperCase();
          if (mer === 'PM' && h !== 12) h += 12;
          if (mer === 'AM' && h === 12) h = 0;
          return h >= 10;
        });

      setAvailableSlots(normalized);

      // If the previously-selected time is no longer selectable, clear it so the
      // customer must re-pick (prevents submitting a now-conflicting slot after
      // they change package / sqft / add-ons).
      if (selectedTime && !normalized.some(s => s.available && s.time === selectedTime)) {
        setSelectedTime('');
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      // Soft default: an infrastructure hiccup must never deny a paying customer.
      // Show the default slots as selectable; the SERVER conflict gate still
      // rejects a genuine overlap at submit, so this cannot cause a double-booking.
      setAvailableSlots(getDefaultTimeSlots().map(t => ({ time: t, available: true })));
    }
  };

  const formatTimeSlot = (timeString) => {
    try {
      // Handle ISO format timestamps (2026-02-05T08:00:00.000Z)
      if (timeString.includes('T') || timeString.includes('Z')) {
        const date = new Date(timeString);
        let hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        
        hours = hours % 12;
        hours = hours ? hours : 12; // 0 should be 12
        
        const minutesStr = minutes < 10 ? '0' + minutes : minutes;
        return `${hours}:${minutesStr} ${ampm}`;
      }
      
      // If already in correct format (8:00 AM), return as is
      if (timeString.match(/^\d{1,2}:\d{2}\s?(AM|PM)$/i)) {
        return timeString;
      }
      
      // If just HH:MM format, add AM/PM
      if (timeString.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = timeString.split(':').map(Number);
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      }
      
      // Default: return as is
      return timeString;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  const getDefaultTimeSlots = () => {
    return [
      '10:00 AM',
      '11:00 AM',
      '12:00 PM',
      '1:00 PM',
      '2:00 PM',
      '3:00 PM',
      '4:00 PM',
      '5:00 PM',
      '6:00 PM'
    ];
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        ...formData,
        appointmentDate: selectedDate,
        appointmentTime: selectedTime,
        packageId: formData.selectedPackage || undefined,
        addOns: addOnsList.filter(a => selectedAddOns[a.id]).map(a => ({ id: a.id, name: a.name, price: a.price })),
        addOnsTotal: addOnsTotal,
        discountCode: discountResult ? discountCode : undefined,
        specialRequests: formData.specialRequests + (addOnsTotal > 0 ? '\n\nAdd-Ons Selected: ' + addOnsList.filter(a => selectedAddOns[a.id]).map(a => `${a.name} ($${a.price})`).join(', ') + ` — Add-Ons Total: $${addOnsTotal}` : '')
      };

      const response = await bookingAPI.completeBooking(bookingData);
      
      const bookingId = response.data?.appointmentId || response.data?.data?._id;

      // GUEST: the booking is a 30-minute hold that must be paid now (Thomas,
      // 2026-09-21). Straight to Stripe; if checkout can't open, the cancel
      // page lets them retry until the hold runs out.
      const hold = response.data?.guestHold;
      if (hold?.token && bookingId) {
        localStorage.removeItem('bookingInProgress');
        try {
          const pay = await axios.post(`${API_URL}/payment/create-checkout-session`, { bookingId: String(bookingId), guestToken: hold.token });
          if (pay.data?.url) {
            window.location.href = pay.data.url;
            return;
          }
        } catch (payErr) {
          console.warn('Guest checkout did not open:', payErr.response?.data?.message || payErr.message);
        }
        navigate(`/payment/cancelled?booking_id=${bookingId}&hold=${encodeURIComponent(hold.token)}`);
        return;
      }
      
      // Payment never blocks a booking; it blocks delivery (Thomas, 2026-09-16).
      // The booking used to redirect straight to Stripe for the full amount.
      // The confirmation page now offers "Pay now" (the invoice checkout —
      // correct after credit and discounts) or pay any time before delivery,
      // with the gallery payment gate enforcing the second half.
      toast.success('Booking confirmed!');
      navigate(`/booking/confirmed/${bookingId || ''}`);
    } catch (error) {
      // Guest used an email that already has an account: send them to sign in
      // with the booking draft kept (Thomas, 2026-09-21).
      if (error.response?.status === 409 && error.response?.data?.code === 'account_exists') {
        setShowGuestChoice(false);
        setAccountExists(true);
        return;
      }
      console.error('Error submitting booking:', error);
      toast.error(error.response?.data?.message || 'Failed to submit booking');
    } finally {
      setLoading(false);
    }
  };

  // async: step 3 awaits a live travel-fee quote from the server before
  // advancing, so the customer sees the real number on the summary.
  const nextStep = async () => {
    // Step 1: Must select a package OR at least one add-on
    if (step === 1 && !formData.selectedPackage && addOnsTotal === 0) {
      toast.error('Please select a package or at least one à la carte service');
      return;
    }

    // Step 2: Client info validation
    if (step === 2 && (!formData.clientName || !formData.clientEmail || !formData.clientPhone)) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Pack A4: steps reordered. Property Info now comes before Schedule
    // so the time picker can show truly accurate availability (knows
    // package + sqft + addons by the time customer reaches Schedule).

    // Step 3: Property Info validation (was Schedule)
    if (step === 3) {
      if (!formData.propertyAddress) {
        toast.error('Please enter the property street address');
        return;
      }
      if (!formData.propertyCity) {
        toast.error('Please enter the city');
        return;
      }
      if (!formData.propertyState) {
        toast.error('Please enter the state');
        return;
      }
      if (!formData.propertyZip) {
        toast.error('Please enter the ZIP code');
        return;
      }
      // Land is priced by acres, so a land package needs them.
      if (isLand && isLandPackage(selectedPkg) && parseAcres(formData.acreage) === null) {
        toast.error('Please enter the parcel size in acres');
        return;
      }
      // Fetch the real travel fee from the server. This used to be a
      // hardcoded city list applying a flat $25, which disagreed with what the
      // server actually charged (Draper, for one, showed free but billed $25).
      // One engine, one answer.
      if (!travelFeeLoading) {
        setTravelFeeLoading(true);
        try {
          const resp = await travelAPI.quote({
            propertyAddress: formData.propertyAddress,
            propertyCity: formData.propertyCity,
            propertyState: formData.propertyState || 'UT',
            propertyZip: formData.propertyZip
          });
          const info = resp?.data?.data || {};
          setTravelFee(Number(info.travelFee) || 0);
          setTravelInfo(info);
          setIsUtahCounty(!!info.withinFreeZone);
        } catch (e) {
          console.error('Travel fee check error:', e);
          // Don't block the booking — the server computes the authoritative
          // fee at submission and the total is confirmed there.
          setTravelFee(0);
          setTravelInfo({ unavailable: true });
          setIsUtahCounty(false);
        } finally {
          setTravelFeeLoading(false);
        }
      }
    }

    // Step 4: Schedule validation (was Property Info)
    if (step === 4 && (!selectedDate || !selectedTime)) {
      toast.error('Please select a date and time');
      return;
    }

    // After step 4 (Schedule): signed in = submit. Not signed in = choose
    // guest checkout (pay now) or sign in (credit, discounts, pay later).
    if (step === 4) {
      const token = localStorage.getItem('token');
      if (!token) {
        setShowGuestChoice(true);
        return;
      }
      handleSubmit();
      return;
    }

    setStep(step + 1);
  };

  // The old step-4 behaviour, now one of the two guest-choice buttons.
  const goToSignIn = (message = 'Sign in or create an account to finish your booking') => {
    localStorage.setItem('bookingInProgress', JSON.stringify({
      formData,
      selectedDate: selectedDate?.toISOString(),
      selectedTime,
      selectedAddOns,
      discountCode: discountResult ? discountCode : '',
      step: 4
    }));
    localStorage.setItem('redirectAfterLogin', '/book');
    navigate('/client/login', {
      state: {
        message,
        from: '/book',
        email: formData.clientEmail || ''
      }
    });
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="book-page">
      <div className="book-hero">
        <div className="container">
          <h1>Book a Photo Session</h1>
          <p>Professional real estate photography made easy</p>
        </div>
      </div>

      <div className="container book-content">
        {/* Progress Steps */}
        <div className="booking-steps">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Select Package</div>
          </div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Your Information</div>
          </div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Property Details</div>
          </div>
          <div className={`step ${step >= 4 ? 'active' : ''}`}>
            <div className="step-number">4</div>
            <div className="step-label">Schedule</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="booking-form">
          {/* Step 1: Select Package */}
          {step === 1 && (
            <div className="step-content">
              <h2>Choose Your Photography Package</h2>
              <div className="listing-kind" role="radiogroup" aria-label="What are you listing?">
                <button type="button" role="radio" aria-checked={!isLand}
                  className={`listing-kind-option${!isLand ? ' is-selected' : ''}`}
                  onClick={() => chooseListingKind('home')}>
                  Home or building
                </button>
                <button type="button" role="radio" aria-checked={isLand}
                  className={`listing-kind-option${isLand ? ' is-selected' : ''}`}
                  onClick={() => chooseListingKind('land')}>
                  Land or lot
                </button>
              </div>
              <div className={`packages-grid${isLand ? ' is-land' : ''}`} role="radiogroup" aria-label="Photography packages">
                {visiblePackages.map(pkg => (
                  <div
                    key={pkg._id}
                    className={`package-card ${formData.selectedPackage === pkg._id ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, selectedPackage: pkg._id })}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFormData({ ...formData, selectedPackage: pkg._id }); } }}
                    role="radio"
                    aria-checked={formData.selectedPackage === pkg._id}
                    tabIndex={0}
                    aria-label={`${pkg.name} package - $${pkg.price}`}
                  >
                    <h3>{pkg.name}</h3>
                    <div className="price">${pkg.price}</div>
                    <p className="description">{pkg.description}</p>
                    <ul className="features">
                      {pkg.features?.map((feature, idx) => (
                        feature.startsWith('*') ? (
                          <li key={idx} style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic', listStyle: 'none', marginTop: '6px' }}>{feature}</li>
                        ) : (
                          <li key={idx}>✓ {feature}</li>
                        )
                      ))}
                    </ul>
                  </div>
                ))}
                {isLand && visiblePackages.length > 0 && (
                  <div className="package-card land-quote">
                    <h3>Priced by size</h3>
                    <p className="description">
                      ${visiblePackages[0].price} covers the first acre. Each additional acre, or part of one, adds ${landCfg.pricePerAcre} and at least {landCfg.aerialPhotosPerAcre} more aerial photos.
                    </p>
                    <ul className="features land-examples">
                      {[0.5, 2, 5].map(a => (
                        <li key={a}>{a === 0.5 ? '½' : a} acre{a > 1 ? 's' : ''} — ${landPriceFor(a)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* À La Carte Add-Ons — always visible */}
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.3rem', marginBottom: '0.5rem', color: '#1a1a2e' }}>À La Carte Services</h3>
                <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  {formData.selectedPackage ? 'Enhance your package with additional services.' : 'Select any services individually, or pair them with a package above.'}
                </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                    {visibleAddOns.map(addon => {
                      const once = includedOnce.has(addon.id);
                      const units = includedUnitsFor(selectedPkg, addon.id);
                      return (
                      <label key={addon.id} style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '12px 14px', borderRadius: '8px', cursor: once ? 'default' : 'pointer',
                        border: selectedAddOns[addon.id] ? '2px solid #2563eb' : '1px solid #e5e7eb',
                        background: once ? '#f9fafb' : (selectedAddOns[addon.id] ? '#eff6ff' : 'white'),
                        transition: 'all 0.15s'
                      }}>
                        <input
                          type="checkbox"
                          checked={once || !!selectedAddOns[addon.id]}
                          disabled={once}
                          onChange={() => toggleAddOn(addon.id)}
                          style={{ width: '18px', height: '18px', accentColor: '#2563eb' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#1a1a2e' }}>{addon.name}</span>
                            <span style={{ fontWeight: '700', color: once ? '#059669' : '#2563eb', fontSize: '0.9rem', whiteSpace: 'nowrap', marginLeft: '8px' }}>{once ? 'Included' : `+$${addon.price}`}</span>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '2px' }}>
                            {once ? `Included in your ${pkgShortName} package` : units ? `${addon.description} — extra, on top of the ${units} in your ${pkgShortName} package` : addon.description}
                          </div>
                        </div>
                      </label>
                      );
                    })}
                  </div>

                  {/* Discount Code */}
                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1a1a2e', display: 'block', marginBottom: '6px' }}>Have a discount code?</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(''); setDiscountResult(null); }}
                        placeholder="Enter code"
                        style={{ flex: 1, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.9rem', textTransform: 'uppercase' }}
                      />
                      <button type="button" onClick={applyDiscount} disabled={applyingDiscount || !discountCode.trim()} style={{
                        padding: '10px 20px', background: '#1a1a2e', color: 'white', border: 'none',
                        borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem', opacity: (!discountCode.trim() || applyingDiscount) ? 0.5 : 1
                      }}>{applyingDiscount ? '...' : 'Apply'}</button>
                    </div>
                    {discountError && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '6px' }}>{discountError}</div>}
                    {discountResult && <div style={{ color: '#059669', fontSize: '0.85rem', marginTop: '6px' }}>✓ {discountResult.description || discountResult.code}: -${discountResult.discountAmount.toFixed(2)} off</div>}
                  </div>

                  {/* Running Total */}
                  <div style={{
                    marginTop: '1rem', padding: '16px 20px', background: '#f9fafb',
                    borderRadius: '10px', border: '1px solid #e5e7eb',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#1a1a2e' }}>Estimated Total</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        {selectedPkg ? `${selectedPkg.name}: $${selectedPkg.price}` : 'No package selected'}
                        {addOnsTotal > 0 && ` + $${addOnsTotal} add-ons`}
                        {discountAmount > 0 && ` − $${discountAmount.toFixed(2)} discount`}
                      </div>
                    </div>
                    <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#2563eb' }}>
                      ${estimatedTotal}
                    </div>
                  </div>
                </div>

              <div className="form-actions">
                <button type="button" onClick={nextStep} className="next-btn">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Client Information */}
          {step === 2 && (
            <div className="step-content">
              <h2>Your Contact Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="clientEmail"
                    value={formData.clientEmail}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    name="clientPhone"
                    value={formData.clientPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, clientPhone: formatPhoneNumber(e.target.value) }))}
                    required
                    placeholder="(801) 555-1234"
                  />
                </div>

                <div className="form-group">
                  <label>How did you hear about us?</label>
                  <select
                    name="hearAboutUs"
                    value={formData.hearAboutUs}
                    onChange={handleChange}
                  >
                    <option value="google">Google Search</option>
                    <option value="referral">Referral</option>
                    <option value="social">Social Media</option>
                    <option value="realtor">Real Estate Agent</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={prevStep} className="back-btn">
                  Back
                </button>
                <button type="button" onClick={nextStep} className="next-btn">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Schedule (was previously Step 3) */}
          {step === 4 && (
            <div className="step-content">
              <h2>Select Date and Time</h2>
              <div className="schedule-grid">
                <div className="calendar-section">
                  <label>Select Date</label>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => {
                      try {
                        setSelectedDate(date);
                      } catch (error) {
                        console.error('Date selection error:', error);
                      }
                    }}
                    minDate={(() => {
                      // Pack A3.4: bump minDate forward by minNoticeHours.
                      // If minNoticeHours=24 and it's 11pm, tomorrow's date is
                      // still less than 24h away, so the next pickable date is
                      // the day after. This is approximate at the date level;
                      // exact hour-level enforcement lives in the time picker
                      // (greyed-out unavailable slots) and the backend conflict gate.
                      const minHours = bookingPolicy?.minNoticeHours;
                      if (typeof minHours === 'number' && minHours > 0) {
                        return new Date(Date.now() + minHours * 3_600_000);
                      }
                      return new Date();
                    })()}
                    filterDate={(date) => {
                      // Pack A3.4: hide weekend dates when policy disables them.
                      // Backend /availability also enforces this; this is the
                      // upstream UX so customers don't fill out 4 form steps
                      // before being rejected.
                      if (bookingPolicy && bookingPolicy.allowWeekendBookings === false) {
                        const day = date.getDay(); // 0=Sun, 6=Sat (local time, matches picker)
                        if (day === 0 || day === 6) return false;
                      }
                      return true;
                    }}
                    inline
                    calendarClassName="custom-calendar"
                  />
                </div>

                <div className="time-slots-section">
                  <label>Select Time <span style={{ fontWeight: 400, color: '#6b7280' }}>(Mountain Time)</span></label>
                  {selectedDate ? (
                    availableSlots.some(s => s.available) ? (
                      <div className="time-slots">
                        {availableSlots.map((slot, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`time-slot ${selectedTime === slot.time ? 'selected' : ''}${!slot.available ? ' unavailable' : ''}`}
                            onClick={() => slot.available && setSelectedTime(slot.time)}
                            disabled={!slot.available}
                            aria-disabled={!slot.available}
                            title={!slot.available ? 'Unavailable — overlaps another scheduled shoot' : undefined}
                            style={!slot.available ? { opacity: 0.4, textDecoration: 'line-through', cursor: 'not-allowed' } : undefined}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="no-slots">No times available, please choose another date.</p>
                    )
                  ) : (
                    <p className="select-date-first">Please select a date first</p>
                  )}
                </div>
              </div>

              {accountExists && (
                <div className="guest-choice guest-choice-exists" role="alert">
                  <div className="guest-choice-option">
                    <h4>An account with that email already exists</h4>
                    <p>Sign in to finish your booking. Everything you've entered is saved, and "Forgot password" is on the sign-in screen if you need it.</p>
                    <button type="button" className="submit-btn" onClick={() => goToSignIn('An account with that email already exists. Sign in to finish your booking.')}>Sign in</button>
                  </div>
                </div>
              )}

              {showGuestChoice && (
                <div className="guest-choice" role="region" aria-label="How would you like to finish booking?">
                  <div className="guest-choice-option">
                    <h4>Continue as a guest</h4>
                    <p>Pay now to confirm. Your time is held for 30 minutes while you check out, and we'll email you a link to set up your account.</p>
                    <button type="button" className="submit-btn" disabled={loading} onClick={() => { setShowGuestChoice(false); handleSubmit(); }}>
                      {loading ? 'Submitting...' : 'Continue to payment'}
                    </button>
                  </div>
                  <div className="guest-choice-option">
                    <h4>Sign in or create an account</h4>
                    <p>Use account credit or a discount code, and pay any time before your photos are delivered.</p>
                    <button type="button" className="back-btn" onClick={() => goToSignIn()}>Sign in</button>
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={prevStep} className="back-btn">
                  Back
                </button>
                <button type="button" onClick={nextStep} className="submit-btn" disabled={loading}>
                  {loading ? 'Submitting...' : 'Complete Booking'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Property Details (was previously Step 4) */}
          {step === 3 && (
            <div className="step-content">
              <h2>Property Information</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                    required
                    placeholder="123 Main St"
                  />
                </div>

                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="propertyCity"
                    value={formData.propertyCity}
                    onChange={handleChange}
                    required
                    placeholder="Provo"
                  />
                </div>

                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    name="propertyState"
                    value={formData.propertyState}
                    onChange={handleChange}
                    required
                    maxLength={2}
                    placeholder="UT"
                  />
                </div>

                <div className="form-group">
                  <label>ZIP Code *</label>
                  <input
                    type="text"
                    name="propertyZip"
                    value={formData.propertyZip}
                    onChange={handleChange}
                    required
                    maxLength={10}
                    placeholder="84601"
                  />
                </div>

                {isLand ? (
                  <>
                    <div className="form-group">
                      <label>Property Type</label>
                      <input type="text" value="Land" readOnly aria-readonly="true" />
                    </div>

                    <div className="form-group">
                      <label>Acres{isLandPackage(selectedPkg) ? ' *' : ''}</label>
                      <input
                        type="number"
                        name="acreage"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={formData.acreage}
                        onChange={handleChange}
                        placeholder="0.46"
                      />
                      {isLandPackage(selectedPkg) && landAcres !== null && (
                        <p className="acreage-hint">
                          {landAcres} acre{landAcres === 1 ? '' : 's'}: ${selectedPkg.price + acreageSurcharge} — at least {landAerialPhotoMinimum(landAcres)} aerial photos covering the whole parcel
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Property Type</label>
                      <select
                        name="propertyType"
                        value={formData.propertyType}
                        onChange={handleChange}
                      >
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                        <option value="luxury">Luxury Home</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Square Footage</label>
                      <input
                        type="number"
                        name="squareFootage"
                        value={formData.squareFootage}
                        onChange={handleChange}
                        placeholder="2500"
                      />
                    </div>

                    <div className="form-group">
                      <label>Bedrooms</label>
                      <input
                        type="number"
                        name="bedrooms"
                        value={formData.bedrooms}
                        onChange={handleChange}
                        placeholder="3"
                      />
                    </div>

                    <div className="form-group">
                      <label>Bathrooms</label>
                      <input
                        type="number"
                        step="0.5"
                        name="bathrooms"
                        value={formData.bathrooms}
                        onChange={handleChange}
                        placeholder="2.5"
                      />
                    </div>
                  </>
                )}

                <div className="form-group full-width">
                  <label>Special Requests or Notes</label>
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Any special requirements, access instructions, or additional information..."
                  ></textarea>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={prevStep} className="back-btn">
                  Back
                </button>
                <button type="button" onClick={nextStep} className="next-btn">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Pricing Summary - shown on steps 2-4 */}
          {step >= 2 && selectedPkg && (
            <div className="pricing-summary-panel">
              <h3>Order Summary</h3>

              {bundleUpsell && (
                <div style={{
                  background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px',
                  padding: '14px 16px', margin: '0 0 14px'
                }}>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#15803d', marginBottom: '4px' }}>
                    Save ${bundleUpsell.saving.toFixed(2)} with the {bundleUpsell.pkg.name.replace(' Package', '')} package
                  </div>
                  <div style={{ fontSize: '0.84rem', color: '#3f6212', lineHeight: 1.5 }}>
                    It already includes {bundleUpsell.covered.map(a => a.name.replace(/^Video — /, '')).join(', ')}
                    {' '}&mdash; so you'd pay <strong>${bundleUpsell.upgradeTotal.toFixed(2)}</strong> instead, and get everything else in that tier too.
                  </div>
                  <button
                    type="button"
                    onClick={applyBundleUpsell}
                    style={{
                      marginTop: '10px', padding: '8px 16px', borderRadius: '6px', border: 'none',
                      background: '#15803d', color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer'
                    }}
                  >
                    Switch to {bundleUpsell.pkg.name.replace(' Package', '')}
                  </button>
                </div>
              )}
              <div className="summary-row">
                <span>{selectedPkg.name}</span>
                <span>${selectedPkg.price?.toFixed(2)}</span>
              </div>
              {addOnsTotal > 0 && (
                <>
                  <div className="summary-divider" />
                  <div className="summary-label">Add-Ons:</div>
                  {addOnsList.filter(a => selectedAddOns[a.id]).map(a => (
                    <div className="summary-row summary-addon" key={a.id}>
                      <span>{a.name}</span>
                      <span>+${a.price.toFixed(2)}</span>
                    </div>
                  ))}
                </>
              )}
              {acreageSurcharge > 0 && (
                <>
                  <div className="summary-divider" />
                  <div className="summary-row">
                    <span>Additional acreage ({landAcres} acres · at least {landAerialPhotoMinimum(landAcres)} aerial photos)</span>
                    <span>+${acreageSurcharge.toFixed(2)}</span>
                  </div>
                </>
              )}
              {sqftSurcharge > 0 && (
                <>
                  <div className="summary-divider" />
                  <div className="summary-row">
                    <span>Large property ({formData.squareFootage} sq ft){sqftBonusPhotos > 0 ? ` · +${sqftBonusPhotos} photos` : ''}</span>
                    <span>+${sqftSurcharge.toFixed(2)}</span>
                  </div>
                </>
              )}
              {discountAmount > 0 && (
                <>
                  <div className="summary-divider" />
                  <div className="summary-row summary-discount">
                    <span>Discount ({discountCode})</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                </>
              )}
              {travelFee > 0 && (
                <>
                  <div className="summary-divider" />
                  <div className="summary-row">
                    <span>
                      Travel Fee
                      {travelInfo?.distance > 0 && (
                        <span style={{ color: '#666', fontWeight: 400 }}> ({travelInfo.distance} mi)</span>
                      )}
                    </span>
                    <span>+${travelFee.toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="summary-divider" />
              <div className="summary-row summary-total">
                <span>Estimated Total</span>
                <span>${estimatedTotal.toFixed(2)}</span>
              </div>

              {/* Pre-purchase expiry disclosure.
                  This is the placement that matters most: Utah treats an
                  undisclosed expiry on purchased goods or services as a
                  deceptive practice, and a term first surfaced in the delivery
                  email is disclosed AFTER the money changed hands. Quiet, but
                  adjacent to the total, where the eye already is. */}
              <div style={{
                fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.5,
                marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb'
              }}>
                Galleries stay online {mediaExpiryDays} days from delivery. 3D tours are hosted a
                minimum of {mediaExpiryDays} days and may be removed after that as capacity
                requires. Please download and keep your own copies &mdash; extended hosting is
                available on request.
              </div>

              {/* Pack J.1: subscription savings inline pitch. Shows only when
                  a sub-eligible package is selected AND customer is not
                  already a subscriber. Frames savings in dollars per month
                  (higher emotional weight than per-shoot). */}
              {showCreditNudge && (
                <div style={{
                  marginTop: '14px', padding: '12px 14px',
                  background: '#fef9c3', border: '1px solid #fde68a',
                  borderRadius: '6px', fontSize: '0.85rem', color: '#78350f'
                }}>
                  <strong>Book often?</strong> Prepaid credit packs add bonus credit on top of what you put in, and it never expires.{' '}
                  <a href="/credit" style={{ color: '#92400e', fontWeight: '600', textDecoration: 'underline' }}>See credit packs</a>
                </div>
              )}

              {travelFeeLoading && (
                <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '10px', fontStyle: 'italic' }}>
                  Checking travel distance...
                </p>
              )}
              {!travelFeeLoading && travelInfo?.withinFreeZone && travelFee === 0 && (
                <p style={{ fontSize: '0.8rem', color: '#15803d', marginTop: '10px' }}>
                  No travel fee — this property is inside {travelInfo.freeZone || 'the free service area'}.
                </p>
              )}
              {!travelFeeLoading && travelInfo?.estimated && (
                <p style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '10px' }}>
                  We couldn't verify the exact distance for this address, so the minimum travel fee is shown.
                  It's confirmed when your booking is submitted.
                </p>
              )}
              {!travelFeeLoading && travelInfo?.unavailable && (
                <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '10px', fontStyle: 'italic' }}>
                  Travel fee will be confirmed when your booking is submitted.
                </p>
              )}
              {!travelFeeLoading && travelFee > 0 && !travelInfo?.estimated && (
                <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '10px', fontStyle: 'italic' }}>
                  Travel fee applies to properties outside {travelInfo?.freeZone || 'the free service area'}.
                </p>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Book;
